import { useState, type ReactElement } from 'react';
import { Calendar } from '@/components/Calendar/Calendar';
import useEmployee from '@/hooks/employee/useEmployee';
import useSWR from 'swr';
import { type EmployeeSchedules } from '@/schemas/API/GET/employee/schedule/response';
import { getFetcher } from '@/api/fetchers';
import { Flex } from '@mantine/core';
import dayjs, { type Dayjs } from 'dayjs';
import { employeeScheduleToCalendarDays } from '@/utils/scheduleUtils';
import SectionErrorNotification from '@/components/Section/SectionErrorNotification';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import { TaskDetailModal, type TaskType } from '../Modals/TaskDetailModal';

interface EmployeeScheduleCalendarProps {
  allowMonthSwitching?: boolean; // Toggles month switching, defaults to false
}

export const EmployeeScheduleCalendar = ({
  allowMonthSwitching = false,
}: EmployeeScheduleCalendarProps): ReactElement => {
  const { employee, error } = useEmployee();
  const [date, setDate] = useState<Dayjs>(dayjs().set('date', 1));

  // SonarLint: Both value and setter are used; false positive.
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskType | null>(null);

  // Fetch the schedule data
  const schedule = useSWR<EmployeeSchedules>(
    employee
      ? `employees/${employee.id}/schedule?year=${date.year()}&month=${date.month() + 1}`
      : null,
    getFetcher
  );

  if (error) return <SectionErrorNotification />;
  if (schedule.error) return <SectionErrorNotification />;
  if (!schedule.data) return <SectionLoadingOverlay />;
  if (schedule.data.year !== date.year()) return <SectionErrorNotification />;
  if (schedule.data.month !== date.month() + 1)
    return <SectionErrorNotification />;

  const handleTaskClick = (task: TaskType) => {
    setSelectedTask(task);
    setModalOpen(true);
  };

  return (
    <>
      <Flex wrap="nowrap" justify="flex-start" align="flex-start" gap="md">
        <Calendar
          month={date}
          setMonth={allowMonthSwitching ? setDate : undefined}
          days={employeeScheduleToCalendarDays(schedule.data, handleTaskClick)}
        />
      </Flex>

      <TaskDetailModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        task={selectedTask}
      />
    </>
  );
};
